import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useDataContext } from 'context/DataStoreContext';
import { useApi } from 'model/Api';
import { User } from 'model/User';
import { Ledger } from 'model/Ledger';
import { Contractor } from 'model/Contractor';
import { generalStyle } from 'helper/Style';
import { convertCosts, convertDate, convertUserName } from 'helper/Converter';
import Layout from 'component/part/Layout';
import BackLink from 'component/part/BackLink';
import H2 from 'component/part/H2';
import List from 'component/part/List';

type ListLedger = Ledger & {
  leaderName: string;
  safetyOfficerName: string;
  primeContractorName: string;
  totalCosts: ReturnType<typeof convertCosts>,
  grossProfit: number;
};

const ledgerConditions = { with: ['cost'] };

const ledgerIndexes = [
  { label: '現場名', key: 'name' },
  { label: '発注元', key: 'orderingParty' },
  { label: '略称', key: 'shortName' },
  { label: '上長', key: 'safetyOfficerId' },
  { label: '現場代理人', key: 'leaderId' },
  { label: '弊社次数', key: 'order' },
  { label: '元請', key: 'primeContractorId' },
  { label: '請負協力業者' },
  { label: '工期', key: 'endDate' },
  { label: '請負金額', key: 'price' },
  { label: '人工' },
  { label: '機械' },
  { label: '産廃' },
  { label: '経費' },
  { label: '費用小計' },
  { label: '粗利' },
];

const style = {
  listArea: {
    overflowX: 'scroll' as const,
  },
  list: {
    ...generalStyle.list(16, { right: [10, 11, 12, 13, 14, 15, 16] }),
    whiteSpace: 'nowrap',
    fontSize: '1.4rem',
  },
}

const ScheduleList: React.FC = () => {
  const { state } = useDataContext();
  const { list = [] } = state?.ledger || {};
  const api = useApi();
  const [users, setUsers] = useState<User[]>([]);
  const [ledgers, setLedgers] = useState<ListLedger[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);

  useEffect(() => {
    (async () => {
      const [usersResponse, contractorsResponse] = await Promise.all([
        api.user.getsByOwn(),
        api.contractor.getsByOwn(),
      ]);
      setUsers(usersResponse);
      setContractors(contractorsResponse);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const listLedgers: ListLedger[] = list.map((ledger) => {
        const leader = users.find((user) => user.id === ledger.leaderId);
        const safetyOfficer = users.find((user) => user.id === ledger.safetyOfficerId);
        const contractor = contractors.find((contractor) => contractor.id === ledger.primeContractorId);
        const totalCosts = convertCosts(ledger.costs);

        return {
          ...ledger,
          leaderName: leader ? convertUserName(leader) : '',
          safetyOfficerName: safetyOfficer ? convertUserName(safetyOfficer) : '',
          primeContractorName: contractor?.name || '',
          totalCosts,
          grossProfit: ledger.price - totalCosts.total,
        };
      });
      setLedgers(listLedgers);
    })();
  }, [list]);

  return <Layout>
    <BackLink link='/' label='トップ'></BackLink>
    <H2>工事着工表</H2>
    <div css={style.listArea}>
      <List
        type='ledger'
        conditions={ledgerConditions}
        indexes={ledgerIndexes}
        css={style.list}
      >
        {ledgers.map((ledger, index) => (
          <div css={generalStyle.listRow} key={index}>
            <div>
              <NavLink to={`/ledger/${ledger.id}`} css={generalStyle.link}>{ledger.name}</NavLink>
            </div>
            <div>{ledger.orderingParty}</div>
            <div>{ledger.shortName}</div>
            <div>{ledger.safetyOfficerName}</div>
            <div>{ledger.leaderName}</div>
            <div>{ledger.order ? `${ledger.order}次` : '元請'}</div>
            <div>{ledger.primeContractorName}</div>
            <div>
              {(ledger.members || []).map((partner, partnerIndex) => (
                <div key={partnerIndex}>{partner.name || ''}</div>
              ))}
            </div>
            <div>{convertDate(ledger.startDate, 'list')}〜{convertDate(ledger.endDate, 'list')}</div>
            <div>&yen;{ledger.price.toLocaleString()}</div>
            <div>&yen;{ledger.totalCosts.personal.toLocaleString()}</div>
            <div>&yen;{ledger.totalCosts.macinery.toLocaleString()}</div>
            <div>&yen;{ledger.totalCosts.industrialWaste.toLocaleString()}</div>
            <div>&yen;{ledger.totalCosts.other.toLocaleString()}</div>
            <div>&yen;{ledger.totalCosts.total.toLocaleString()}</div>
            <div>&yen;{ledger.grossProfit.toLocaleString()}</div>
          </div>
        ))}
      </List>
    </div>
  </Layout>;
};

export default ScheduleList;
